/**
 * Tiện ích xây dựng menu - Menu Builder Utilities
 *
 * Chức năng:
 * - Xây dựng cây menu từ danh sách menu items
 * - Gán icon cho các mục menu
 * - Xử lý menu items theo cấu trúc cha-con
 */
import { ComponentType, ReactNode, createElement } from 'react';
import type { MenuProps } from 'antd';
import {
  TeamOutlined,
  CalendarOutlined,
  MedicineBoxOutlined,
  DollarOutlined,
  InboxOutlined,
  BellOutlined,
  SettingOutlined,
  BarChartOutlined,
  FileDoneOutlined,
  DashboardOutlined,
} from '@ant-design/icons';

export interface MenuItemData {
  id: number;
  key: string;
  label: string;
  path?: string;
  icon?: string;
  parentKey?: string;
  displayOrder: number;
}

export type AppMenuItem = NonNullable<MenuProps['items']>[number];

// Keep this file as .ts (no JSX) by mapping to icon components.
const iconMap: Record<string, ComponentType> = {
  DashboardOutlined,
  TeamOutlined,
  CalendarOutlined,
  MedicineBoxOutlined,
  DollarOutlined,
  FileDoneOutlined,
  InboxOutlined,
  BarChartOutlined,
  BellOutlined,
  SettingOutlined,
};

function getIcon(iconName?: string): ReactNode {
  if (!iconName || !iconMap[iconName]) {
    return undefined;
  }
  return createElement(iconMap[iconName]);
}

export function buildMenuTree(
  items: MenuItemData[],
  allowedKeys: string[]
): NonNullable<MenuProps['items']> {
  // Filter items that are allowed
  const allowedItems = items.filter((item) => allowedKeys.includes(item.key));

  if (allowedItems.length === 0) {
    return [];
  }

  // Get root items (no parent)
  const rootItems = allowedItems
    .filter((item) => !item.parentKey)
    .sort((a, b) => a.displayOrder - b.displayOrder);

  return rootItems.map((rootItem) => {
    const children = allowedItems
      .filter((item) => item.parentKey === rootItem.key)
      .sort((a, b) => a.displayOrder - b.displayOrder)
      .map((child) => ({
        key: child.path || child.key,
        label: child.label,
      })) as AppMenuItem[];

    return {
      key: rootItem.path || rootItem.key,
      label: rootItem.label,
      icon: getIcon(rootItem.icon || undefined),
      ...(children.length > 0 ? { children } : {}),
    } as AppMenuItem;
  });
}
